import React, { useState } from 'react';
import '../styles/AgentPlayground.css';

const AgentPlayground = ({ onInteraction }) => {
  const [selectedAgent, setSelectedAgent] = useState('content');
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interactions, setInteractions] = useState([]);

  // Demo agents with their sample capabilities
  const agents = {
    content: {
      name: "Content Creator",
      description: "Generate blog posts, social media content, and marketing copy.",
      prompt: "Write a short social media post about...",
      icon: "✍️"
    },
    outreach: {
      name: "Influencer Outreach",
      description: "Find and connect with relevant influencers for your product.",
      prompt: "Find influencers for my product...",
      icon: "🤝"
    },
    social: {
      name: "Social Media Manager",
      description: "Create and schedule posts across multiple platforms.",
      prompt: "Create a content plan for...",
      icon: "📱"
    }
  };

  // Sample responses for the demo
  const demoResponses = {
    content: {
      default: "✨ Ready to transform your content strategy? Just type what you need above.",
      responses: {
        "travel blog": "🌴 Exploring new horizons! Today we're diving into the hidden gems of Bali that most tourists miss. From secret waterfalls to local culinary delights, our journey takes us beyond the usual tourist traps. Stay tuned for our full guide dropping tomorrow! #TravelDeeper #BaliSecrets",
        "new product": "🚀 Exciting news! We've just launched our game-changing product that solves [specific pain point]. Designed with you in mind, it features [key benefit] and [unique feature]. Early adopters are already seeing [specific result]! Link in bio to learn more. #Innovation #ProductLaunch",
        "fitness": "💪 Your reminder that consistency beats perfection! Small daily habits lead to major transformations. What's one small fitness goal you're committing to this week? Comment below and let's hold each other accountable! #FitnessJourney #SmallStepsHugeResults"
      }
    },
    outreach: {
      default: "🔍 Let me help you find the perfect influencers for your brand. Describe your product or service.",
      responses: {
        "fitness app": "📊 Found 5 ideal influencers for your fitness app:\n\n1. @FitWithJamie - 250K followers, 3.2% engagement rate\n2. @HealthyHabitsByAlex - 180K followers, 5.7% engagement rate\n3. @WorkoutWithTina - 420K followers, 2.9% engagement rate\n4. @NutritionMasterJeff - 150K followers, 6.1% engagement rate\n5. @DailyFitnessMotivation - 320K followers, 4.2% engagement rate\n\nShall I generate personalized outreach messages for these influencers?",
        "skincare": "📊 Found 5 ideal influencers for your skincare brand:\n\n1. @GlowWithMaria - 340K followers, 4.1% engagement rate\n2. @SkincareScienceWithDr.Lee - 210K followers, 7.3% engagement rate\n3. @BeautyEssentialsByJade - 390K followers, 3.8% engagement rate\n4. @OrganicBeautyRoutine - 180K followers, 5.9% engagement rate\n5. @SkinTipsWithTaylor - 280K followers, 4.7% engagement rate\n\nShall I generate personalized outreach messages for these influencers?",
        "coffee": "📊 Found 5 ideal influencers for your coffee brand:\n\n1. @CaffeineAndCreativity - 190K followers, 4.8% engagement rate\n2. @MorningBrewWithJack - 240K followers, 3.9% engagement rate\n3. @CoffeeConnoisseurClub - 320K followers, 3.5% engagement rate\n4. @BaristaTipsAndTricks - 170K followers, 6.2% engagement rate\n5. @EthicalCoffeeTravels - 210K followers, 5.3% engagement rate\n\nShall I generate personalized outreach messages for these influencers?"
      }
    },
    social: {
      default: "📅 Need help planning your social media content? Tell me about your business and goals.",
      responses: {
        "marketing agency": "📊 7-Day Content Plan for Marketing Agency:\n\n𝐌𝐨𝐧𝐝𝐚𝐲: Case study highlight - showcase recent client success\n𝐓𝐮𝐞𝐬𝐝𝐚𝐲: Industry tip - share actionable marketing advice\n𝐖𝐞𝐝𝐧𝐞𝐬𝐝𝐚𝐲: Team spotlight - introduce team member\n𝐓𝐡𝐮𝐫𝐬𝐝𝐚𝐲: Trendjacking - relate current trend to marketing\n𝐅𝐫𝐢𝐝𝐚𝐲: Client testimonial - share positive feedback\n𝐒𝐚𝐭𝐮𝐫𝐝𝐚𝐲: Behind-the-scenes - show agency culture\n𝐒𝐮𝐧𝐝𝐚𝐲: Weekly marketing tip newsletter teaser\n\nOptimal posting times: Instagram (11am, 3pm), LinkedIn (9am, 2pm), Twitter (8am, 1pm, 5pm)",
        "restaurant": "📊 7-Day Content Plan for Restaurant:\n\n𝐌𝐨𝐧𝐝𝐚𝐲: Weekly specials announcement + mouth-watering photo\n𝐓𝐮𝐞𝐬𝐝𝐚𝐲: Behind-the-scenes kitchen prep video\n𝐖𝐞𝐝𝐧𝐞𝐬𝐝𝐚𝐲: Chef's tip or simple recipe to try at home\n𝐓𝐡𝐮𝐫𝐬𝐝𝐚𝐲: Throwback to restaurant history or milestone\n𝐅𝐫𝐢𝐝𝐚𝐲: Weekend special teaser + reservation reminder\n𝐒𝐚𝐭𝐮𝐫𝐝𝐚𝐲: Customer spotlight/repost customer content\n𝐒𝐮𝐧𝐝𝐚𝐲: Ingredient spotlight - showcase quality/local sourcing\n\nOptimal posting times: Instagram (12pm, 6pm), Facebook (11am, 7pm), TikTok (short food prep videos at 2pm, 8pm)",
        "e-commerce": "📊 7-Day Content Plan for E-commerce Store:\n\n𝐌𝐨𝐧𝐝𝐚𝐲: Product spotlight with unique use case\n𝐓𝐮𝐞𝐬𝐝𝐚𝐲: Customer review/testimonial highlight\n𝐖𝐞𝐝𝐧𝐞𝐬𝐝𝐚𝐲: Product tutorial or how-to guide\n𝐓𝐡𝐮𝐫𝐬𝐝𝐚𝐲: Industry or product education post\n𝐅𝐫𝐢𝐝𝐚𝐲: Flash sale or limited promotion\n𝐒𝐚𝐭𝐮𝐫𝐝𝐚𝐲: Lifestyle content showing product in use\n𝐒𝐮𝐧𝐝𝐚𝐲: User-generated content spotlight\n\nOptimal posting times: Instagram (11am, 7pm), Pinterest (8pm, 9pm), Facebook (1pm, 5pm)"
      }
    }
  };

  const handleAgentSelect = (agentKey) => {
    setSelectedAgent(agentKey);
    setResult(demoResponses[agentKey].default);
    setUserInput('');
    
    // Track the agent selection
    if (onInteraction) {
      onInteraction(agentKey, null, 'agent_selected');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    setIsLoading(true);

    // Simplified interaction tracking
    if (onInteraction) {
      onInteraction(selectedAgent, userInput, null); // Just track which agent and input, not all response data
    }

    // Save this interaction
    const newInteraction = {
      agent: selectedAgent,
      input: userInput,
      timestamp: new Date().toISOString()
    };
    setInteractions([...interactions, newInteraction]);

    // Simulate AI processing with a delay
    setTimeout(() => {
      // Check for specific keywords to provide "intelligent" responses
      const input = userInput.toLowerCase();
      let response = "I'm sorry, I don't have enough information to help with that. Could you provide more details?";

      // Check for keyword matches in the responses object
      Object.keys(demoResponses[selectedAgent].responses).forEach(keyword => {
        if (input.includes(keyword)) {
          response = demoResponses[selectedAgent].responses[keyword];
        }
      });

      setResult(response);
      setIsLoading(false);
      
      // Track the interaction and response
      if (onInteraction) {
        onInteraction(selectedAgent, userInput, response);
      }
    }, 1500);
  };

  const handleTryAgain = () => {
    setResult(null);
    setUserInput('');
  };

  const scrollToContact = () => {
    document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="agent-playground" id="agent-playground">
      <div className="container">
        <h2><span className="highlight">Test Drive</span> Our AI Agents</h2>
        <p className="section-subtitle">
          Experience the power of our AI agents with this interactive demo
        </p>
        
        <div className="playground-container">
          <div className="agent-tabs">
            {Object.entries(agents).map(([key, agent]) => (
              <div 
                key={key} 
                className={`agent-tab ${selectedAgent === key ? 'active' : ''}`}
                onClick={() => handleAgentSelect(key)}
              >
                <div className="tab-icon">{agent.icon}</div>
                <div className="tab-info">
                  <h3>{agent.name}</h3>
                  <p>{agent.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="agent-workspace">
            <div className="workspace-header">
              <h3>{agents[selectedAgent].name} <span className="status-dot"></span></h3>
              <div className="demo-label">Demo Preview</div>
            </div>
            
            <div className="workspace-body">
              {result ? (
                <div className="agent-result">
                  <div className="result-content">
                    {result}
                  </div>
                  <div className="result-actions">
                    <button onClick={handleTryAgain} className="secondary-button">Try Again</button>
                    <button onClick={scrollToContact} className="primary-button">Get Full Access</button>
                  </div>
                </div>
              ) : (
                <div className="agent-input-area">
                  <form onSubmit={handleSubmit}>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        placeholder={agents[selectedAgent].prompt}
                        disabled={isLoading}
                      />
                      <button 
                        type="submit" 
                        className="submit-input-button"
                        disabled={isLoading || !userInput.trim()}
                      >
                        {isLoading ? '...' : '→'}
                      </button>
                    </div>
                  </form>
                  
                  {isLoading && (
                    <div className="loading-indicator">
                      <div className="loading-spinner"></div>
                      <p>AI agent thinking...</p>
                    </div>
                  )}
                  
                  <div className="sample-prompts">
                    <p>Try asking about:</p>
                    <div className="prompt-suggestions">
                      {selectedAgent === 'content' && (
                        <>
                          <button onClick={() => setUserInput('Write a travel blog post')}>travel blog</button>
                          <button onClick={() => setUserInput('Create a post for my new product')}>new product</button>
                          <button onClick={() => setUserInput('Write a fitness motivation post')}>fitness</button>
                        </>
                      )}
                      {selectedAgent === 'outreach' && (
                        <>
                          <button onClick={() => setUserInput('Find influencers for my fitness app')}>fitness app</button>
                          <button onClick={() => setUserInput('Influencers for skincare products')}>skincare</button>
                          <button onClick={() => setUserInput('Find coffee influencers')}>coffee</button>
                        </>
                      )}
                      {selectedAgent === 'social' && (
                        <>
                          <button onClick={() => setUserInput('Content plan for marketing agency')}>marketing agency</button>
                          <button onClick={() => setUserInput('Social media plan for restaurant')}>restaurant</button>
                          <button onClick={() => setUserInput('Content schedule for e-commerce')}>e-commerce</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="workspace-footer">
              <p>This is a limited demo. Get full access to all 100+ AI agents for your business.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgentPlayground;
